/** SearchInput: a search field with a leading icon and an optional trailing slot (a key hint, a Clear button). */
import type { ComponentProps, ReactNode, Ref } from "react";
import { cx } from "./cx";
import { Icon, type IconName } from "./icons";

interface SearchInputProps extends Omit<ComponentProps<"input">, "type" | "className"> {
  /** The field's accessible name; also the placeholder unless one is given. */
  label: string;
  /** `border` on the page and inside a card body, `input` on a card frame. */
  tone?: "border" | "input";
  /** The soft drop shadow of a field that sits on a tinted surface. */
  raised?: boolean;
  /** The leading icon; a table toolbar's field has its own drawing. */
  icon?: Extract<IconName, "search" | "table-search">;
  trailing?: ReactNode;
  className?: string;
  ref?: Ref<HTMLInputElement>;
}

export function SearchInput({ label, tone = "border", raised = false, icon = "search", trailing, className, placeholder = label, ...props }: SearchInputProps) {
  return (
    <label
      data-slot="input-group"
      className={cx(
        "group/input flex h-9 cursor-text items-center gap-2 overflow-clip rounded-lg border-hair bg-card py-2 pl-3 pr-2.5 transition-[border-color,box-shadow] duration-150 hover:border-border-hover focus-within:border-ring focus-within:shadow-field-focus",
        tone === "input" ? "border-input" : "border-border",
        raised && "shadow-field",
        className,
      )}
    >
      <Icon name={icon} />
      <input
        data-slot="input-group-input"
        type="search"
        aria-label={label}
        placeholder={placeholder}
        className="min-w-0 flex-1 bg-transparent text-body leading-none text-foreground outline-none placeholder:text-muted-foreground [&::-webkit-search-cancel-button]:hidden"
        {...props}
      />
      {trailing}
    </label>
  );
}

/** A key hint that fades out while the field is in use. Put it in `trailing`. */
export function SearchInputHint({ children }: { children: ReactNode }) {
  return (
    <span data-slot="input-group-addon" aria-hidden className="flex shrink-0 items-center transition-opacity group-focus-within/input:opacity-0">
      {children}
    </span>
  );
}
